import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import SiteNav from "@/components/landing/Nav";
import DynamicForm from "@/components/forms/DynamicForm";
import { api } from "@/lib/api";

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState(null);

  useEffect(() => {
    api.get(`/orders/${orderId}`)
      .then(({ data }) => setOrder(data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
    api.get("/forms?purpose=contact")
      .then(({ data }) => setContactForm(data[0] || null))
      .catch(() => {});
  }, [orderId]);

  return (
    <div data-testid="order-confirmation-page" className="min-h-screen bg-ink text-foreground">
      <Seo noindex title="Order Confirmed — AB Hunter Consulting USA" siteName="AB Hunter Consulting USA" description="Order confirmation." />
      <SiteNav />
      <div className="lg:pl-64">
        <main className="hero-glow grain relative flex min-h-screen items-center justify-center px-5 py-24">
          {loading ? (
            <Loader2 className="h-8 w-8 animate-spin text-gold" data-testid="confirmation-loading" />
          ) : (
            <div className="w-full max-w-lg space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 24, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-3xl border border-gold/25 bg-ink-card/80 p-8 backdrop-blur-xl sm:p-10"
              >
                {!order ? (
                  <div className="text-center" data-testid="confirmation-not-found">
                    <p className="font-display text-2xl font-semibold">Order not found</p>
                    <p className="mt-3 text-sm text-muted-foreground">Double-check your link, or contact engage@abhunter.us.</p>
                  </div>
                ) : (
                  <>
                    <div className="text-center">
                      <CheckCircle2 data-testid="confirmation-icon" className="mx-auto h-16 w-16 text-gold" />
                      <h1 className="mt-5 font-display text-3xl font-semibold">
                        {order.status === "paid" ? "Thank you!" : "Order received"}
                      </h1>
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                        {order.status === "paid"
                          ? `Your payment is confirmed${order.customer?.name ? `, ${order.customer.name.split(" ")[0]}` : ""}. A receipt was sent to ${order.customer?.email}.`
                          : "Your order is being processed — you'll receive an update by email."}
                      </p>
                    </div>
                    <div className="mt-8 space-y-3 rounded-2xl border border-gold/15 bg-ink p-5" data-testid="confirmation-items">
                      {order.items?.map((item, i) => (
                        <div key={i} className="text-sm">
                          <div className="flex items-center justify-between gap-4">
                            <span className="min-w-0 flex-1 truncate">{item.name}</span>
                            <span className="text-muted-foreground">×{item.quantity}</span>
                            <span className="font-semibold text-gold">${(item.unit_price * item.quantity).toFixed(2)}</span>
                          </div>
                          {item.attendees?.length > 0 && (
                            <p className="mt-1 text-xs text-muted-foreground">Attendees: {item.attendees.join(", ")}</p>
                          )}
                        </div>
                      ))}
                      <div className="flex items-center justify-between border-t border-gold/15 pt-3">
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Total paid</span>
                        <span data-testid="confirmation-total" className="font-display text-xl font-semibold text-gold">${Number(order.amount).toFixed(2)}</span>
                      </div>
                      {order.promo_code && (
                        <p className="text-right text-xs text-emerald-300" data-testid="confirmation-promo">Promo {order.promo_code} saved you ${Number(order.discount || 0).toFixed(2)}</p>
                      )}
                      {order.shipping && (
                        <p className="border-t border-gold/10 pt-2 text-xs text-muted-foreground" data-testid="confirmation-shipping">
                          Shipping to: {order.shipping.address1}{order.shipping.address2 ? `, ${order.shipping.address2}` : ""}, {order.shipping.city}, {order.shipping.state} {order.shipping.zip}
                        </p>
                      )}
                    </div>
                    {order.items?.some((i) => i.type === "event") && (
                      <p className="mt-5 rounded-xl border border-gold/20 bg-gold/5 px-4 py-3 text-center text-xs leading-relaxed text-foreground/80">
                        Your event registration is confirmed — event details will follow by email. Questions? Call 225.603.4116.
                      </p>
                    )}
                  </>
                )}
                <Button data-testid="confirmation-home-button" asChild className="mt-8 w-full rounded-full bg-gold py-6 font-semibold text-ink hover:bg-gold-hover">
                  <Link to="/">Back to AB Hunter</Link>
                </Button>
              </motion.div>

              {order && contactForm && (
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
                  className="rounded-3xl border border-gold/20 bg-ink-card/70 p-8 backdrop-blur-xl sm:p-10"
                  data-testid="thankyou-form-card"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">While you're here</span>
                  <h2 className="mt-2 font-display text-xl font-semibold">{contactForm.name}</h2>
                  <p className="mt-1.5 text-sm text-muted-foreground">Tell us how else we can help — we read every message.</p>
                  <div className="mt-5">
                    <DynamicForm form={contactForm} testIdPrefix="thankyou" />
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
